import { ReactNode } from 'react';

import {
  IconButton as ButtonIcon,
  Link,
  Typography as Text,
  Tooltip,
} from '@material-ui/core';

import {
  RiDeleteBinLine,
  RiExternalLinkLine,
  RiFileLine,
  RiPencilLine,
} from '@remixicon/react';

import {
  DateUtils,
  DialogCompositeMui as DialogComposite,
  Spinner,
  useDialog,
} from '@spotify/confidence-core-react';
import { useExternalDocumentRef } from '@spotify/confidence-external-documents-react';
import {
  AttachmentFragment,
  CollaborationV1ExternalDocumentSourceType,
  ExternalDocumentRefFragment,
} from '@spotify/confidence-graphql';
import {
  createArtifactExtension,
  createModifierClickHandler,
  isExternalDocument,
  isSafeUrl,
} from '@spotify/plugin-confidence-artifacts-react';
import { MarkdownContent } from '@spotify/plugin-confidence-chat-react';
import {
  ArtifactCardBase,
  ArtifactIcon,
  ArtifactListItem,
  ArtifactMenu,
  getGradientStartColor,
  getResourceName,
} from '@spotify/plugin-confidence-projects-react';

import { projectsPlugin } from '../plugin';

import styles from './ExternalDocumentArtifactExtension.module.css';

const getSourceLabel = (
  type?: CollaborationV1ExternalDocumentSourceType,
): string => {
  switch (type) {
    case CollaborationV1ExternalDocumentSourceType.GoogleDocs:
      return 'Google Docs';
    case CollaborationV1ExternalDocumentSourceType.Notion:
      return 'Notion';
    default:
      return 'External document';
  }
};

const getSafeUrl = (url: string | null | undefined): string | undefined =>
  url && isSafeUrl(url) ? url : undefined;

const OpenInSourceButton = ({
  sourceUrl,
  sourceLabel,
}: {
  sourceUrl: string | undefined;
  sourceLabel: string;
}) => {
  if (!sourceUrl) {
    return null;
  }

  return (
    <Tooltip title={`Open in ${sourceLabel}`}>
      <ButtonIcon
        size="small"
        onClick={() => window.open(sourceUrl, '_blank', 'noopener,noreferrer')}
        aria-label={`Open in ${sourceLabel}`}
      >
        <RiExternalLinkLine size={16} />
      </ButtonIcon>
    </Tooltip>
  );
};

const ExternalDocumentMarkdownPreview = ({
  externalDocument,
  isLoading,
}: {
  externalDocument: ExternalDocumentRefFragment | undefined;
  isLoading: boolean;
}) => {
  if (isLoading && !externalDocument) {
    return (
      <div className={styles.loading}>
        <Spinner size={24} aria-label="Loading document" />
      </div>
    );
  }

  if (externalDocument?.markdown) {
    return <MarkdownContent>{externalDocument.markdown}</MarkdownContent>;
  }

  const safeSourceUrl = getSafeUrl(externalDocument?.source?.uri);
  const sourceLabel = getSourceLabel(externalDocument?.source?.type);

  return (
    <div className={styles.emptyState}>
      <Text variant="body2" color="textSecondary">
        No preview available.
      </Text>
      {safeSourceUrl && (
        <Link href={safeSourceUrl} target="_blank" rel="noopener noreferrer">
          Open in {sourceLabel}
        </Link>
      )}
    </div>
  );
};

const ExternalDocumentContent = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => {
  const resourceName = getResourceName(artifact.uri);
  const { externalDocument, isLoading } = useExternalDocumentRef({
    name: resourceName,
  });

  return (
    <ExternalDocumentMarkdownPreview
      externalDocument={externalDocument}
      isLoading={isLoading}
    />
  );
};

const ExternalDocumentHeaderActions = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => {
  const resourceName = getResourceName(artifact.uri);
  const { externalDocument } = useExternalDocumentRef({ name: resourceName });
  const safeSourceUrl = getSafeUrl(externalDocument?.source?.uri);
  const sourceLabel = getSourceLabel(externalDocument?.source?.type);

  return (
    <OpenInSourceButton sourceUrl={safeSourceUrl} sourceLabel={sourceLabel} />
  );
};

const ExternalDocumentCard = ({
  artifact,
  onDelete,
}: {
  artifact: AttachmentFragment;
  onDelete?: () => void;
}) => {
  const { openDialog, closeDialog } = useDialog();
  const resourceName = getResourceName(artifact.uri);
  const { externalDocument } = useExternalDocumentRef({ name: resourceName });
  const safeSourceUrl = getSafeUrl(externalDocument?.source?.uri);
  const sourceLabel = getSourceLabel(externalDocument?.source?.type);

  const handleNavigate = () => {
    openDialog({
      content: (
        <DialogComposite.Root static onClose={closeDialog}>
          <DialogComposite.Content>
            <DialogComposite.CloseButton />
            <DialogComposite.Header>
              <DialogComposite.Title>
                {artifact.displayName ||
                  externalDocument?.title ||
                  'Untitled document'}
              </DialogComposite.Title>
            </DialogComposite.Header>
            <DialogComposite.Body className={styles.dialogBody}>
              <div className={styles.dialogActions}>
                <OpenInSourceButton
                  sourceUrl={safeSourceUrl}
                  sourceLabel={sourceLabel}
                />
              </div>
              <ExternalDocumentContent artifact={artifact} />
            </DialogComposite.Body>
          </DialogComposite.Content>
        </DialogComposite.Root>
      ),
      dialogProps: { maxWidth: 'md', fullWidth: true },
    });
  };

  return (
    <ArtifactCardBase
      artifact={artifact}
      resolvedDisplayName={externalDocument?.title ?? undefined}
      onNavigate={handleNavigate}
      onDelete={onDelete}
    />
  );
};

const ExternalDocumentListItem = ({
  artifact,
  icon,
  onSelect,
  onDelete,
  onRename,
}: {
  artifact: AttachmentFragment;
  icon: ReactNode;
  onSelect: () => void;
  onDelete?: () => void;
  onRename?: () => void;
}) => {
  const resourceName = getResourceName(artifact.uri);
  const { externalDocument } = useExternalDocumentRef({ name: resourceName });
  const safeSourceUrl = getSafeUrl(externalDocument?.source?.uri);
  const sourceLabel = getSourceLabel(externalDocument?.source?.type);
  const dateLabel = DateUtils.toDateString(
    new Date(artifact.createTime),
    DateUtils.MONTH_DAY_FORMAT,
  );
  const handleOpenExternal = () => {
    if (safeSourceUrl) {
      window.open(safeSourceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <ArtifactListItem.Root
      onClick={createModifierClickHandler(safeSourceUrl, onSelect)}
    >
      <ArtifactListItem.Icon>
        <ArtifactIcon>{icon}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={
          artifact.displayName || externalDocument?.title || 'Untitled document'
        }
        secondary={
          <>
            {sourceLabel} &middot; {dateLabel}
          </>
        }
      />
      <ArtifactListItem.Action>
        <ArtifactMenu.Root aria-label="Document actions">
          <ArtifactMenu.Item
            textValue="Open document"
            icon={<RiFileLine size={16} />}
            onClick={onSelect}
          >
            Open document
          </ArtifactMenu.Item>
          {safeSourceUrl && (
            <ArtifactMenu.Item
              textValue={`Open in ${sourceLabel}`}
              icon={<RiExternalLinkLine size={16} />}
              onClick={handleOpenExternal}
            >
              Open in {sourceLabel}
            </ArtifactMenu.Item>
          )}
          {onRename && (
            <ArtifactMenu.Item
              textValue="Rename"
              icon={<RiPencilLine size={16} />}
              onClick={onRename}
            >
              Rename
            </ArtifactMenu.Item>
          )}
          {onDelete && (
            <ArtifactMenu.Item
              textValue="Delete"
              icon={<RiDeleteBinLine size={16} />}
              onClick={onDelete}
              destructive
            >
              Delete
            </ArtifactMenu.Item>
          )}
        </ArtifactMenu.Root>
      </ArtifactListItem.Action>
    </ArtifactListItem.Root>
  );
};

export const ExternalDocumentArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'external-document',
    predicate: artifact => isExternalDocument(artifact.uri),
    icon: () => ({
      Icon: RiFileLine,
      color: getGradientStartColor('blue'),
    }),
    label: 'Document',
    category: 'Documents',
    render: artifact => <ExternalDocumentContent artifact={artifact} />,
    renderHeaderActions: artifact => (
      <ExternalDocumentHeaderActions artifact={artifact} />
    ),
    renderListItem: (artifact, { onSelect, onDelete, onRename, icon }) => (
      <ExternalDocumentListItem
        artifact={artifact}
        icon={icon}
        onSelect={onSelect}
        onDelete={onDelete}
        onRename={onRename}
      />
    ),
    renderCard: (artifact, { onDelete }) => (
      <ExternalDocumentCard artifact={artifact} onDelete={onDelete} />
    ),
  }),
);
